import { useState } from "react";
import { Download, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { firmsToMarkdown, downloadBlob } from "@/lib/export";
import { useLocale } from "@/context/LocaleContext";
import AlertsBell from "./AlertsBell";

/**
 * Thin global toolbar pinned at the top of every authenticated page.
 * - Alerts bell (unread watchlist alerts)
 * - Language toggle (EN / CS)
 * - Download (full library → Markdown)
 * - Print (opens browser print dialog; print-only stylesheet handles styling)
 * - Small "Made with Emergent" logo link (replaces the platform's bottom-right badge,
 *   which we hide via CSS in index.css). Preserves attribution while keeping the
 *   bottom-right corner clean for app content.
 */
export default function TopToolbar() {
    const [downloading, setDownloading] = useState(false);
    const { locale, setLocale, t } = useLocale();

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const { data: firms } = await api.get("/firms");
            const name = `affynix-library-${new Date().toISOString().slice(0, 10)}.md`;
            downloadBlob(firmsToMarkdown(firms || []), name, "text/markdown;charset=utf-8");
        } catch {
            // silent — the per-page exports already surface errors when relevant
        } finally {
            setDownloading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const toggleLocale = () => setLocale(locale === "en" ? "cs" : "en");

    return (
        <div
            data-testid="top-toolbar"
            className="print:hidden sticky top-0 z-30 flex items-center justify-end gap-1.5 border-b border-zinc-800 bg-black/80 backdrop-blur px-6 py-2"
        >
            <AlertsBell />
            <button
                onClick={toggleLocale}
                data-testid="locale-toggle"
                title={`${t("toolbar.language")}: ${locale.toUpperCase()}`}
                className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-1.5 transition-colors"
            >
                <span className={locale === "en" ? "text-amber-500 font-bold" : ""}>EN</span>
                <span className="text-zinc-700">/</span>
                <span className={locale === "cs" ? "text-amber-500 font-bold" : ""}>CS</span>
            </button>
            <button
                onClick={handleDownload}
                disabled={downloading}
                data-testid="toolbar-download-btn"
                title={t("toolbar.download")}
                className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-1.5 transition-colors disabled:opacity-50"
            >
                <Download className="w-3 h-3" />
                {downloading ? t("toolbar.building") : t("toolbar.download")}
            </button>
            <button
                onClick={handlePrint}
                data-testid="toolbar-print-btn"
                title={t("toolbar.print")}
                className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-1.5 transition-colors"
            >
                <Printer className="w-3 h-3" />
                {t("toolbar.print")}
            </button>
            <a
                href="https://app.emergent.sh/?utm_source=affynix"
                target="_blank"
                rel="noreferrer"
                data-testid="emergent-logo-link"
                title="Made with Emergent"
                className="inline-flex items-center gap-1.5 ml-1 border border-zinc-800 hover:border-amber-500 text-xs font-mono text-zinc-500 hover:text-amber-500 px-2 py-1.5 transition-colors"
            >
                {/* Tiny inline Emergent mark — a thin '>' sign in a square */}
                <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
                    <rect x="1" y="1" width="14" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M5.5 4 L9.5 8 L5.5 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="hidden sm:inline">emergent</span>
            </a>
        </div>
    );
}
